/**
 * Demo Data Connection Context
 * 
 * Provides demo-specific data connection functionality that integrates with the existing
 * data connection system but uses demo data sources.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useModelSelector } from './ModelSelectorContext';
import { useUser } from './UserContext';
import { DemoUsersAPI, DemoUser as APIDemoUser } from '../services/demo-users-api';

export type DemoDataSource = 'sample' | 'demo' | 'live';

export interface DemoUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  roles?: string[];
  created_at: string;
  updated_at: string;
  // Demo-specific properties
  role: string;
  avatar?: string;
}

export interface DemoDataConnectionState {
  // Demo-specific data source
  demoDataSource: DemoDataSource;
  
  // Demo user management
  currentDemoUser: DemoUser | null;
  availableDemoUsers: DemoUser[];
  
  // Demo data status
  isDemoDataLoaded: boolean;
  demoDataError: string | null;
  
  // Demo mode indicators
  isDemoMode: boolean;
  isSampleDataMode: boolean;
}

interface DemoDataConnectionContextType extends DemoDataConnectionState {
  // Data source management
  setDemoDataSource: (source: DemoDataSource) => void;
  switchToDemoDataSource: (source: DemoDataSource) => Promise<void>;
  
  // User management
  setCurrentDemoUser: (user: DemoUser | null) => Promise<void>;
  createDemoUser: (userData: Partial<DemoUser>) => Promise<DemoUser>;
  updateDemoUser: (userId: string, updates: Partial<DemoUser>) => Promise<DemoUser>;
  deleteDemoUser: (userId: string) => Promise<void>;
  
  // Demo data management
  loadDemoData: () => Promise<void>;
  refreshDemoData: () => Promise<void>;
  clearDemoData: () => void;
  
  // Mode helpers
  switchToDemoMode: () => Promise<void>;
  exitDemoMode: () => Promise<void>;
  isUsingDemoData: () => boolean;
}

const DemoDataConnectionContext = createContext<DemoDataConnectionContextType | undefined>(undefined);

interface DemoDataConnectionProviderProps {
  children: ReactNode;
}

export const DemoDataConnectionProvider: React.FC<DemoDataConnectionProviderProps> = ({ children }) => {
  const { dataSource, setDataSource } = useModelSelector();
  const { selectUser } = useUser();
  
  const [demoDataSource, setDemoDataSourceState] = useState<DemoDataSource>('demo');
  const [currentDemoUser, setCurrentDemoUser] = useState<DemoUser | null>(null);
  const [availableDemoUsers, setAvailableDemoUsers] = useState<DemoUser[]>([]);
  const [isDemoDataLoaded, setIsDemoDataLoaded] = useState(false);
  const [demoDataError, setDemoDataError] = useState<string | null>(null);

  // Load demo users from API
  useEffect(() => {
    const loadDemoUsers = async () => {
      try {
        const apiUsers = await DemoUsersAPI.getDemoUsers();
        
        // Transform API users to DemoUser format
        const demoUsers: DemoUser[] = apiUsers.map(user => ({
          ...user,
          role: user.roles?.includes('architect') ? 'CEO' :
                user.roles?.includes('coach') ? 'Financial Analyst' :
                user.roles?.includes('operator') ? 'Operations Manager' :
                'Business Owner',
          avatar: undefined
        }));
        
        setAvailableDemoUsers(demoUsers);
        
        // Set the first demo user as default if none selected
        if (!currentDemoUser && demoUsers.length > 0) {
          const defaultUser = demoUsers[0];
          setCurrentDemoUser(defaultUser);
          // Also update the main UserContext to switch to demo user
          try {
            await selectUser(defaultUser.id);
          } catch (error) {
            console.error('Failed to auto-select demo user:', error);
          }
        }
      } catch (error) {
        console.error('Failed to load demo users:', error);
        setDemoDataError('Failed to load demo users');
        
        // Fallback to hardcoded users (using actual UUIDs from database)
        const fallbackUsers: DemoUser[] = [
          { 
            id: 'b6dd68c7-28ab-4d1b-b7fd-4793692f911c', 
            username: 'demo_user', 
            email: 'demo@krysalis.com', 
            first_name: 'Demo',
            last_name: 'User',
            full_name: 'Demo User', 
            is_active: true,
            is_superuser: false,
            roles: ['demo'],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            role: 'Business Owner'
          }
        ];
        setAvailableDemoUsers(fallbackUsers);
        if (!currentDemoUser) {
          const defaultUser = fallbackUsers[0];
          setCurrentDemoUser(defaultUser);
          // Also update the main UserContext to switch to demo user
          try {
            await selectUser(defaultUser.id);
          } catch (error) {
            console.error('Failed to auto-select fallback demo user:', error);
          }
        }
      }
    };

    loadDemoUsers();
  }, [currentDemoUser]);

  // Data source management
  const setDemoDataSource = useCallback((source: DemoDataSource) => {
    setDemoDataSourceState(source);
    
    // Update the main data source to match demo requirements
    if (source === 'demo' || source === 'sample') {
      setDataSource('sample');
    } else {
      setDataSource('live');
    }
  }, [setDataSource]);

  const switchToDemoDataSource = useCallback(async (source: DemoDataSource) => {
    try {
      setDemoDataSource(source);
      await loadDemoData();
    } catch (error) {
      console.error('Failed to switch demo data source:', error);
      setDemoDataError('Failed to switch data source');
    }
  }, [setDemoDataSource]);

  // User management
  const setCurrentDemoUserHandler = useCallback(async (user: DemoUser | null) => {
    setCurrentDemoUser(user);
    
    // Also update the main UserContext
    if (user) {
      try {
        await selectUser(user.id);
      } catch (error) {
        console.error('Failed to select user in UserContext:', error);
      }
    }
  }, [selectUser]);

  const createDemoUser = useCallback(async (userData: Partial<DemoUser>): Promise<DemoUser> => {
    const newUser: DemoUser = {
      id: `demo-user-${Date.now()}`,
      username: userData.username || `demo-user-${Date.now()}`,
      email: userData.email || `demo-user-${Date.now()}@krysalis.com`,
      first_name: userData.first_name || 'Demo',
      last_name: userData.last_name || 'User',
      full_name: userData.full_name || 'Demo User',
      is_active: true,
      is_superuser: false,
      roles: ['demo'],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      role: userData.role || 'User',
      avatar: userData.avatar
    };
    
    setAvailableDemoUsers(prev => [...prev, newUser]);
    return newUser;
  }, []);

  const updateDemoUser = useCallback(async (userId: string, updates: Partial<DemoUser>): Promise<DemoUser> => {
    const updatedUser = availableDemoUsers.find(user => user.id === userId);
    if (!updatedUser) {
      throw new Error('Demo user not found');
    }
    
    const updated = { ...updatedUser, ...updates };
    setAvailableDemoUsers(prev => prev.map(user => user.id === userId ? updated : user));
    
    if (currentDemoUser?.id === userId) {
      setCurrentDemoUser(updated);
    }
    
    return updated;
  }, [availableDemoUsers, currentDemoUser]);

  const deleteDemoUser = useCallback(async (userId: string) => {
    if (availableDemoUsers.length <= 1) {
      throw new Error('Cannot delete the last demo user');
    }
    
    setAvailableDemoUsers(prev => prev.filter(user => user.id !== userId));
    
    if (currentDemoUser?.id === userId) {
      const remainingUsers = availableDemoUsers.filter(user => user.id !== userId);
      setCurrentDemoUser(remainingUsers[0] || null);
    }
  }, [availableDemoUsers, currentDemoUser]);

  // Demo data management
  const loadDemoData = useCallback(async () => {
    try {
      setIsDemoDataLoaded(false);
      setDemoDataError(null);
      
      // Simulate loading demo data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setIsDemoDataLoaded(true);
    } catch (error) {
      console.error('Failed to load demo data:', error);
      setDemoDataError('Failed to load demo data');
    }
  }, []);

  const refreshDemoData = useCallback(async () => {
    await loadDemoData();
  }, [loadDemoData]);

  const clearDemoData = useCallback(() => {
    setIsDemoDataLoaded(false);
    setDemoDataError(null);
  }, []);

  // Mode helpers
  const switchToDemoMode = useCallback(async () => {
    await switchToDemoDataSource('demo');
  }, [switchToDemoDataSource]);

  const exitDemoMode = useCallback(async () => {
    setDataSource('live');
    setDemoDataSourceState('live');
    clearDemoData();
  }, [setDataSource, clearDemoData]);

  const isUsingDemoData = useCallback(() => {
    return demoDataSource === 'demo' || demoDataSource === 'sample' || dataSource === 'sample';
  }, [demoDataSource, dataSource]);

  // Computed properties
  const isDemoMode = demoDataSource === 'demo';
  const isSampleDataMode = demoDataSource === 'sample' || dataSource === 'sample';

  const contextValue: DemoDataConnectionContextType = {
    // State
    demoDataSource,
    currentDemoUser,
    availableDemoUsers,
    isDemoDataLoaded,
    demoDataError,
    isDemoMode,
    isSampleDataMode,
    
    // Data source management
    setDemoDataSource,
    switchToDemoDataSource,
    
    // User management
    setCurrentDemoUser: setCurrentDemoUserHandler,
    createDemoUser,
    updateDemoUser,
    deleteDemoUser,
    
    // Demo data management
    loadDemoData,
    refreshDemoData,
    clearDemoData,
    
    // Mode helpers
    switchToDemoMode,
    exitDemoMode,
    isUsingDemoData
  };

  return (
    <DemoDataConnectionContext.Provider value={contextValue}>
      {children}
    </DemoDataConnectionContext.Provider>
  );
};

export const useDemoDataConnection = (): DemoDataConnectionContextType => {
  const context = useContext(DemoDataConnectionContext);
  if (context === undefined) {
    throw new Error('useDemoDataConnection must be used within a DemoDataConnectionProvider');
  }
  return context;
};
